import { Radio, RadioGroup, useFieldState } from 'informed';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';

import type { FormError, FormValue, FormValues } from 'informed';
import type { CSSProperties } from 'react';

interface Props {
  disabled?: boolean;
  error?: string;
  field: string;
  id: string;
  numberOfStars?: number;
  onChange?: (value: number) => void;
  size?: 'small' | 'medium' | 'large' | 'huge';
  style?: CSSProperties;
  validate?: (value: FormValue, values?: FormValues) => FormError;
  validateOnChange?: boolean;
}

const StarsSelector = ({
  disabled,
  error,
  field,
  id,
  numberOfStars = 5,
  onChange,
  size = 'medium',
  style,
  validate,
  validateOnChange
}: Props) => {
  const { messages, theme } = useReduxState();

  const { value } = useFieldState<number>(field);

  // the stars are laid out in reverse so that hovering one can fill the
  // ones before it, so the highest rating has to come first
  const ratings = Array.from(
    { length: numberOfStars },
    (_, index) => numberOfStars - index
  );

  return (
    <div className={`stars${disabled ? ' disabled' : ''}`} style={style}>
      <div className="radio-group">
        <RadioGroup
          field={field}
          onValueChange={onChange}
          validate={validate}
          validateOnChange={validateOnChange}
        >
          {ratings.map((rating) => (
            <div className="radio" key={rating}>
              <Radio
                disabled={disabled}
                field={field}
                id={`stars-selector-${id}-${rating}`}
                value={rating}
              />
              <label
                className="label"
                htmlFor={`stars-selector-${id}-${rating}`}
                style={{
                  fontSize: {
                    small: '1.5rem',
                    medium: '1.6rem',
                    large: '1.8rem',
                    huge: '2.2rem'
                  }[size]
                }}
              >
                <div
                  aria-label={`${rating} ${messages.tasksStars}`}
                  className={`icon ${value >= rating ? 'active' : ''}`}
                >
                  <div className="filled">
                    <Icon icon="star" fixedWidth={true} />
                  </div>
                  <div className="empty">
                    <Icon icon={['far', 'star']} fixedWidth={true} />
                  </div>
                </div>
              </label>
            </div>
          ))}
        </RadioGroup>
      </div>

      {error ? (
        <div
          className="error-message"
          style={{
            fontSize: {
              small: '1.2rem',
              medium: '1.3rem',
              large: '1.4rem',
              huge: '1.6rem'
            }[size]
          }}
        >
          {error}
        </div>
      ) : null}

      <style jsx>{`
        .stars {
          display: inline-block;
        }
        .radio-group {
          display: inline-flex;
          flex-direction: row-reverse;
        }
        .radio {
          direction: rtl;
          display: inline-block;
          position: relative;
        }
        .radio:not(:first-child) {
          padding-right: 2px;
        }
        .radio > :global(input) {
          opacity: 0;
          overflow: hidden;
          position: absolute;
          height: 0.1px;
          width: 0.1px;
          z-index: -1;
        }
        .label {
          color: ${theme.textColorLight};
          cursor: pointer;
          display: inline-block;
          font-size: 1.6rem;
          font-weight: 400;
          position: relative;
          white-space: normal;
          width: auto;
          word-wrap: break-word;
        }
        .radio > :global(input:focus + label) {
          box-shadow: 0 0 0 1px ${theme.textColorLight};
        }
        .icon {
          display: inline-block;
        }
        .icon .filled {
          display: none;
        }
        .icon.active .filled {
          display: inline-block;
        }
        .icon.active .empty {
          display: none;
        }
        /* hovering only previews a rating that can still be picked, so a
           disabled selector keeps showing the one that was already given */
        .stars:not(.disabled) .radio:hover .filled,
        .stars:not(.disabled) .radio:hover ~ .radio .filled {
          display: inline-block;
        }
        .stars:not(.disabled) .radio:hover .empty,
        .stars:not(.disabled) .radio:hover ~ .radio .empty {
          display: none;
        }
        .stars.disabled .label {
          cursor: default;
        }
        .stars.disabled .icon {
          opacity: 0.5;
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          padding: 3px;
        }
      `}</style>
    </div>
  );
};

export default StarsSelector;
